import {
  CloudinaryFileButtonInput,
  Form,
  SubmitButton,
  TextAreaAutosize as TextArea,
  TextInput,
} from '@components/shared/forms';
import { UploadApiResponse as CloudinaryResponse } from 'cloudinary';
import { Button, Modal, Notification } from '@components/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import useEnv from '@shared/useEnv';
import { ErrorResponse, Tourbook, NewTourbook, TourbookSummary } from '@root/types';
import { Image } from 'cloudinary-react';
import tourbooksActions from '@store/actions/tourbooks';
import actions from '@store/actions/tourbookPage';
import { useDispatch, connect } from 'react-redux';
import cn from 'classnames';
import labelStyles from '@styles/labels.module.less';
import { CompanyLogo } from '@components/shared/CustomIcon/icons';
import { Affix } from 'antd';
import OnlyInMobile from '@components/shared/OnlyInMobile';
import OnlyInDesktopTablet from '@components/shared/OnlyInDesktopTablet';
import s from './EditTourbookModal.module.less';

const mapDispatch = {
  tourbookEdit: () => actions.tourbookEdit(),
};

const connector = connect(null, mapDispatch);

type ModalAction = 'create' | 'edit';

type Props = {
  tourbook: Tourbook | NewTourbook;
  isModalOpen: boolean;
  onCloseModal: () => void;
  onCreateSuccess?: (tourbook: TourbookSummary) => void;
  tourbookEdit: () => void;
  actionType?: ModalAction;
};

type CloudinaryIdTypes = 'header' | 'clientLogo';

const EditTourbookModal = ({
  tourbook,
  isModalOpen,
  onCloseModal,
  onCreateSuccess = () => {},
  tourbookEdit = () => {},
  actionType = 'edit',
}: Props) => {
  const { t } = useTranslation('tourbook');
  const dispatch = useDispatch();

  const [description, setDescription] = useState(tourbook.description || '');
  const { cloudinaryCdn, cloudinaryCloud } = useEnv();
  const [cloudinaryLoading, setCloudinaryLoading] = useState(false);
  const [headerCloudinaryId, setHeaderCloudinaryId] = useState<string | null>(
    tourbook.headerCloudinaryId,
  );
  const [clientLogoCloudinaryId, setClientLogoCloudinaryId] = useState<string | null>(
    tourbook.clientLogoCloudinaryId,
  );
  const [isFormDirty, setIsFormDirty] = useState(false);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .max(90, t('manageDialogs.nameMaxMessage'))
      .required(t('manageDialogs.requiredNameMessage')),
    description: Yup.string().max(160, t('manageDialogs.descriptionMaxMessage')),
    clientName: Yup.string().nullable().max(50, t('manageDialogs.clientNameMaxMessage')),
  });

  const handleCloudinaryFileInputLoading = (loading: boolean) => {
    setCloudinaryLoading(loading);
  };

  const handleCloudinaryFileInputError = (error: string) => {
    Notification.info({
      title: t('notification.updateError'),
      text: error,
      placement: 'bottomLeft',
      duration: 3.5,
    });
  };

  const onReady = (type: CloudinaryIdTypes) => (response: CloudinaryResponse) => {
    if (type === 'header') {
      setHeaderCloudinaryId(response.public_id);
    } else if (type === 'clientLogo') {
      setClientLogoCloudinaryId(response.public_id);
    }
    setIsFormDirty(true);
  };

  const handleCreate = (data, helpers) => {
    dispatch(
      tourbooksActions.createTourbook(data, {
        onSuccess: (tourbook: TourbookSummary) => {
          Notification.info({
            title: t('toasts.tourbookCreatedTitle'),
            placement: 'bottomLeft',
            duration: 3.5,
          });
          onCloseModal();
          onCreateSuccess(tourbook);
        },

        onError: (response: ErrorResponse) =>
          response.errors.forEach(error => {
            helpers.setFieldError(error.field!, error.message);
          }),
      }),
    );
  };

  const handleEdit = (data, helpers) => {
    dispatch(
      tourbooksActions.updateTourbook(tourbook.id, data, {
        onSuccess: () => {
          Notification.info({
            title: t('notification.updateSuccess'),
            placement: 'bottomLeft',
            duration: 3.5,
          });

          tourbookEdit();
          onCloseModal();
        },

        onError: (response: ErrorResponse) =>
          response.errors.forEach(error => {
            helpers.setFieldError(error.field!, error.message);
          }),
      }),
    );
  };

  const handleSubmit = async (values, helpers) => {
    const data = {
      name: values.name,
      description,
      clientName: values.clientName,
      headerCloudinaryId,
      clientLogoCloudinaryId,
    };

    if (actionType === 'create') {
      handleCreate(data, helpers);
    } else {
      handleEdit(data, helpers);
    }
  };

  const closeModal = () => {
    let confirmClose = true;
    if (isFormDirty) {
      confirmClose = window.confirm(t('common:prompt.unsavedChanges'));
    }

    if (confirmClose) {
      setDescription(tourbook.description ? tourbook.description : '');
      setHeaderCloudinaryId(tourbook.headerCloudinaryId);
      setClientLogoCloudinaryId(tourbook.clientLogoCloudinaryId);
      onCloseModal();
    }
  };

  const updateDescription = (form: any) => e => {
    // https://stackoverflow.com/questions/56613496/issue-with-values-formik/57686391#57686391
    // https://github.com/formium/formik/issues/529
    // this duplicated behavior here with setFieldValue() exists due to peculiarities with the
    // form form retaining stale values
    form.setFieldValue('description', e.target.value);
    form.setFieldTouched('description');
    setDescription(e.target.value);
    setIsFormDirty(true);
  };

  const saveUpdatesButtons = (
    <div className={s.cancelAndSaveWrapper}>
      <div className={s.buttonsWrapper}>
        <Button type="secondary" size="medium" onClick={closeModal}>
          {t('manageDialogs.cancel')}
        </Button>
        <SubmitButton
          data-testid="saveTourbookButton"
          className={s.saveButton}
          disabled={cloudinaryLoading || !isFormDirty}
        >
          {actionType === 'create'
            ? t('manageDialogs.createButtonText')
            : t('manageDialogs.saveButtonText')}
        </SubmitButton>
      </div>
    </div>
  );
  return (
    <Modal
      destroyOnClose
      closeModal={closeModal}
      isOpen={isModalOpen}
      title={actionType === 'create' ? t('createTourbook') : t('manageDialogs.editTourbook')}
      className={s.modalBody}
    >
      <Form
        initialValues={{
          name: tourbook.name,
          description: tourbook.description || '',
          clientName: tourbook.clientName || '',
        }}
        validationSchema={validationSchema}
        id="createTourbookForm"
        onSubmit={handleSubmit}
        setIsDirty={setIsFormDirty}
      >
        <h2 className={s.generalDescription}>{t('manageDialogs.generalInformation')}</h2>
        <TextInput
          required
          name="name"
          labelText={t('manageDialogs.title')}
          containerClass={s.nameFieldContainer}
          autoComplete={false}
          showCharacterCount
          maxLength={90}
        />
        <TextArea
          name="description"
          labelText={t('manageDialogs.description')}
          containerClass={s.descriptionFieldContainer}
          className={s.descriptionTextArea}
          value={description}
          onChange={updateDescription}
          onFocus={() => {}}
          onBlur={() => {}}
          showCharacterCount
          maxLength={160}
        />
        <div className={s.headerPreviewImageContainer}>
          <div className={s.headerPreviewImageTitle}>
            <h2 className={s.headerTitle}>{t('manageDialogs.photoHeader')}</h2>
            <div className={s.photoButtonsWrapper}>
              <CloudinaryFileButtonInput
                name="headerCloudinaryId"
                onLoading={handleCloudinaryFileInputLoading}
                onReady={onReady('header')}
                onError={handleCloudinaryFileInputError}
                buttonClassName={s.uploadPhoto}
                buttonType="secondary"
                buttonSize="medium"
                containerClass={s.uploadPhotoContainer}
              />
              {headerCloudinaryId && (
                <button
                  data-testid="removeHeaderImage"
                  type="button"
                  className={s.coverPhotoEdit}
                  onClick={() => {
                    setIsFormDirty(true);
                    setHeaderCloudinaryId('');
                  }}
                >
                  {t('manageDialogs.removeHeaderPhoto')}
                </button>
              )}
            </div>
          </div>
          {headerCloudinaryId ? (
            <Image
              cloudName={cloudinaryCloud}
              privateCdn={cloudinaryCdn}
              publicId={headerCloudinaryId}
              alt={t('tourbookBackgroundImage')}
            />
          ) : (
            <div className={s.editEmptyImagePlaceholderWrapper}>
              <CompanyLogo
                data-testid="emptyHeaderImageIcon"
                className={s.editEmptyImagePlaceholder}
              />
            </div>
          )}
        </div>
        <h2 className={s.generalDescription}>{t('manageDialogs.clientInformation')}</h2>
        <TextInput
          name="clientName"
          labelText={t('manageDialogs.clientName')}
          autoComplete={false}
        />
        <label className={cn(labelStyles.labelText, s.labelText)}>
          {t('manageDialogs.clientLogo')}
        </label>
        <div className={s.clientLogoWrapper}>
          {clientLogoCloudinaryId ? (
            <Image
              cloudName={cloudinaryCloud}
              privateCdn={cloudinaryCdn}
              publicId={clientLogoCloudinaryId}
              alt={t('tourbookClientLogoImage')}
              className={s.companyLogo}
              crop="pad"
              width="200"
              height="200"
            />
          ) : (
            <div className={s.companyLogoIconWrapper}>
              <CompanyLogo data-testid="companyLogoIcon" className={s.companyLogoIcon} />
            </div>
          )}
          <div className={s.photoButtonsWrapper}>
            <CloudinaryFileButtonInput
              name="clientLogoCloudinaryId"
              onLoading={handleCloudinaryFileInputLoading}
              onReady={onReady('clientLogo')}
              onError={handleCloudinaryFileInputError}
              buttonType="secondary"
              buttonSize="medium"
              containerClass={s.uploadPhotoContainer}
              buttonClassName={s.uploadClientLogo}
            />

            {clientLogoCloudinaryId && (
              <button
                data-testid="removeClientLogoImage"
                type="button"
                className={s.removeClientLogo}
                onClick={() => {
                  setIsFormDirty(true);
                  setClientLogoCloudinaryId('');
                }}
              >
                {t('manageDialogs.removeClientLogo')}
              </button>
            )}
          </div>
        </div>

        <OnlyInMobile>
          <Affix className={s.submitButtonPositioner} offsetBottom={0}>
            {saveUpdatesButtons}
          </Affix>
        </OnlyInMobile>
        <OnlyInDesktopTablet>{saveUpdatesButtons}</OnlyInDesktopTablet>
      </Form>
    </Modal>
  );
};

export default connector(EditTourbookModal);
